import React, { useEffect, useState } from 'react';
import { PageHeader, EmptyState } from '@/components/primitives';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Save, PlusCircle, Trash2, ListChecks, Receipt } from 'lucide-react';

export default function AdminSettingsPage() {
  const [tab, setTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companyFilter, setCompanyFilter] = useState('all');
  const [checklists, setChecklists] = useState({ onboarding: [], offboarding: [] });
  const [newTpl, setNewTpl] = useState({ name: '', category: 'recruitment', stage: 'screening', subject: '', body: '', company_id: null });

  const loadTemplates = async () => {
    const url = companyFilter === 'all' ? '/templates' : `/templates?company_id=${companyFilter}`;
    const r = await api.get(url); setTemplates(r.data);
  };
  const loadSettings = async () => {
    const r = await api.get('/admin/settings');
    setChecklists({ onboarding: r.data.default_checklists?.onboarding || [], offboarding: r.data.default_checklists?.offboarding || [] });
  };
  useEffect(() => { api.get('/companies').then((r) => setCompanies(r.data)); loadSettings(); }, []);
  useEffect(() => { loadTemplates(); }, [companyFilter]);

  const saveTemplate = async (t, updates) => {
    try { await api.patch(`/templates/${t.id}`, updates); toast.success('Saved'); loadTemplates(); } catch (e) { toast.error('Save failed'); }
  };
  const delTemplate = async (id) => { await api.delete(`/templates/${id}`); loadTemplates(); };
  const addTemplate = async () => {
    if (!newTpl.name || !newTpl.subject) return toast.error('Name and subject required');
    try { await api.post('/templates', { ...newTpl, company_id: newTpl.company_id || null }); setNewTpl({ name: '', category: 'recruitment', stage: 'screening', subject: '', body: '', company_id: null }); toast.success('Template added'); loadTemplates(); }
    catch (e) { toast.error('Failed'); }
  };

  const saveChecklists = async () => {
    try { await api.patch('/admin/settings/checklists', checklists); toast.success('Default checklists saved'); } catch { toast.error('Save failed'); }
  };

  return (
    <>
      <PageHeader title="Settings & Templates" description="Global email templates, default onboarding/offboarding checklists and app-wide configuration." />
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="settings-tabs">
            <TabsTrigger value="templates">Email templates</TabsTrigger>
            <TabsTrigger value="checklists">Default checklists</TabsTrigger>
            <TabsTrigger value="payslip" data-testid="tab-payslip"><Receipt className="h-3.5 w-3.5 mr-1" />Payslip template</TabsTrigger>
            <TabsTrigger value="smtp">Email delivery</TabsTrigger>
          </TabsList>

          <TabsContent value="templates" className="mt-4 space-y-4">
            <Card className="p-4 space-y-3">
              <p className="font-display font-semibold">Add template</p>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Name</Label><Input value={newTpl.name} onChange={(e) => setNewTpl({ ...newTpl, name: e.target.value })} data-testid="new-tpl-name" /></div>
                <div className="space-y-1"><Label className="text-xs">Category</Label>
                  <Select value={newTpl.category} onValueChange={(v) => setNewTpl({ ...newTpl, category: v })}><SelectTrigger data-testid="new-tpl-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{['recruitment','onboarding','policy','training','offboarding','payroll','other'].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label className="text-xs">Stage (recruitment)</Label>
                  <Select value={newTpl.stage || 'na'} onValueChange={(v) => setNewTpl({ ...newTpl, stage: v === 'na' ? null : v })}><SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="na">-</SelectItem>{['sourced','screening','interview','offer','hired','rejected'].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label className="text-xs">Company (leave blank = global)</Label>
                  <Select value={newTpl.company_id || 'global'} onValueChange={(v) => setNewTpl({ ...newTpl, company_id: v === 'global' ? null : v })}><SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="global">Global</SelectItem>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1 md:col-span-2"><Label className="text-xs">Subject</Label><Input value={newTpl.subject} onChange={(e) => setNewTpl({ ...newTpl, subject: e.target.value })} placeholder="You can use {{candidate_name}} {{company_name}} {{role}}" data-testid="new-tpl-subject" /></div>
                <div className="space-y-1 md:col-span-2"><Label className="text-xs">Body</Label><Textarea rows={5} value={newTpl.body} onChange={(e) => setNewTpl({ ...newTpl, body: e.target.value })} data-testid="new-tpl-body" /></div>
              </div>
              <div className="flex justify-end"><Button onClick={addTemplate} data-testid="new-tpl-save"><PlusCircle className="h-4 w-4 mr-2" />Add</Button></div>
            </Card>

            <div className="flex items-center gap-3">
              <p className="text-sm">Filter:</p>
              <Select value={companyFilter} onValueChange={setCompanyFilter}>
                <SelectTrigger className="w-56 h-9" data-testid="tpl-company-filter"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All</SelectItem>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div className="rounded-xl border bg-card overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Category</TableHead><TableHead>Stage</TableHead><TableHead>Scope</TableHead><TableHead>Subject</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {templates.map((t) => (
                    <TableRow key={t.id} data-testid={`tpl-row-${t.id}`}>
                      <TableCell><Input defaultValue={t.name} onBlur={(e) => e.target.value !== t.name && saveTemplate(t, { name: e.target.value })} className="h-8" /></TableCell>
                      <TableCell><span className="text-xs capitalize">{t.category}</span></TableCell>
                      <TableCell><span className="text-xs capitalize">{t.stage || '—'}</span></TableCell>
                      <TableCell><span className="text-xs">{t.company_id ? (companies.find((c) => c.id === t.company_id)?.name || 'Custom') : 'Global'}</span></TableCell>
                      <TableCell><Input defaultValue={t.subject} onBlur={(e) => e.target.value !== t.subject && saveTemplate(t, { subject: e.target.value })} className="h-8" /></TableCell>
                      <TableCell><Button size="sm" variant="ghost" onClick={() => delTemplate(t.id)}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {templates.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">No templates in this scope.</p>}
            </div>
            <p className="text-xs text-muted-foreground">Editable body content via each template's dialog is supported by the schema; expand as needed. For now edit the subject/name inline and use the AI panel to draft.</p>
          </TabsContent>

          <TabsContent value="checklists" className="mt-4 space-y-4">
            <Card className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="font-display font-semibold flex items-center gap-2"><ListChecks className="h-4 w-4 text-primary" /> Default onboarding checklist</p>
                <Button size="sm" onClick={() => setChecklists({ ...checklists, onboarding: [...checklists.onboarding, 'New step'] })}><PlusCircle className="h-3.5 w-3.5 mr-1" />Add step</Button>
              </div>
              <ul className="space-y-2">
                {checklists.onboarding.map((step, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <Input value={step} onChange={(e) => { const arr = [...checklists.onboarding]; arr[i] = e.target.value; setChecklists({ ...checklists, onboarding: arr }); }} data-testid={`onb-step-${i}`} />
                    <Button size="icon" variant="ghost" onClick={() => setChecklists({ ...checklists, onboarding: checklists.onboarding.filter((_, x) => x !== i) })}><Trash2 className="h-4 w-4" /></Button>
                  </li>
                ))}
              </ul>
            </Card>
            <Card className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="font-display font-semibold flex items-center gap-2"><ListChecks className="h-4 w-4 text-primary" /> Default offboarding checklist</p>
                <Button size="sm" onClick={() => setChecklists({ ...checklists, offboarding: [...checklists.offboarding, 'New step'] })}><PlusCircle className="h-3.5 w-3.5 mr-1" />Add step</Button>
              </div>
              <ul className="space-y-2">
                {checklists.offboarding.map((step, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <Input value={step} onChange={(e) => { const arr = [...checklists.offboarding]; arr[i] = e.target.value; setChecklists({ ...checklists, offboarding: arr }); }} data-testid={`off-step-${i}`} />
                    <Button size="icon" variant="ghost" onClick={() => setChecklists({ ...checklists, offboarding: checklists.offboarding.filter((_, x) => x !== i) })}><Trash2 className="h-4 w-4" /></Button>
                  </li>
                ))}
              </ul>
            </Card>
            <div className="flex justify-end"><Button onClick={saveChecklists} data-testid="save-checklists"><Save className="h-4 w-4 mr-2" />Save defaults</Button></div>
          </TabsContent>

          <TabsContent value="payslip" className="mt-4">
            <PayslipTemplateCard />
          </TabsContent>

          <TabsContent value="smtp" className="mt-4">
            <SMTPConfigCard />
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}

function PayslipTemplateCard() {
  const [f, setF] = useState({
    header_html: '', footer_html: '',
    show_ytd: false, show_employer_contrib: true, show_bank_details: true, show_pan: true,
    disclaimer: '', signature_label: '', company_logo_url: '', accent_color: '#0f766e',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/payslip-template').then((r) => setF({ ...f, ...r.data })).catch(() => null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.post('/admin/payslip-template', f);
      toast.success('Payslip template updated');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Card className="p-5 max-w-3xl space-y-4">
      <div>
        <p className="font-display font-semibold">Payslip template</p>
        <p className="text-sm text-muted-foreground">Applies to every downloaded payslip. Changes take effect immediately - open a payslip to preview.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <div className="space-y-1"><Label>Accent colour</Label><Input type="color" value={f.accent_color || '#0f766e'} onChange={(e) => setF({ ...f, accent_color: e.target.value })} className="h-10 w-24 p-1" data-testid="tpl-accent" /></div>
        <div className="space-y-1"><Label>Company logo URL</Label><Input value={f.company_logo_url || ''} onChange={(e) => setF({ ...f, company_logo_url: e.target.value })} placeholder="https://…/logo.png" data-testid="tpl-logo" /></div>
        <div className="space-y-1"><Label>Signature label</Label><Input value={f.signature_label || ''} onChange={(e) => setF({ ...f, signature_label: e.target.value })} placeholder="Authorised signatory" data-testid="tpl-signature" /></div>
        <div className="grid grid-cols-2 gap-2">
          <label className="flex items-center justify-between rounded-lg border p-2"><span className="text-sm">Show PAN</span><Switch checked={!!f.show_pan} onCheckedChange={(v) => setF({ ...f, show_pan: v })} data-testid="tpl-show-pan" /></label>
          <label className="flex items-center justify-between rounded-lg border p-2"><span className="text-sm">Show bank</span><Switch checked={!!f.show_bank_details} onCheckedChange={(v) => setF({ ...f, show_bank_details: v })} data-testid="tpl-show-bank" /></label>
          <label className="flex items-center justify-between rounded-lg border p-2"><span className="text-sm">Employer PF/ESI</span><Switch checked={!!f.show_employer_contrib} onCheckedChange={(v) => setF({ ...f, show_employer_contrib: v })} data-testid="tpl-show-empcon" /></label>
          <label className="flex items-center justify-between rounded-lg border p-2"><span className="text-sm">YTD figures</span><Switch checked={!!f.show_ytd} onCheckedChange={(v) => setF({ ...f, show_ytd: v })} data-testid="tpl-show-ytd" /></label>
        </div>
      </div>

      <div className="space-y-1"><Label>Header HTML (optional)</Label><Textarea rows={2} value={f.header_html || ''} onChange={(e) => setF({ ...f, header_html: e.target.value })} placeholder="<h4 style='margin:0'>Confidential - for employee only</h4>" data-testid="tpl-header" /></div>
      <div className="space-y-1"><Label>Footer HTML (optional)</Label><Textarea rows={2} value={f.footer_html || ''} onChange={(e) => setF({ ...f, footer_html: e.target.value })} placeholder="For queries, contact hr@company.com" data-testid="tpl-footer" /></div>
      <div className="space-y-1"><Label>Disclaimer</Label><Textarea rows={2} value={f.disclaimer || ''} onChange={(e) => setF({ ...f, disclaimer: e.target.value })} data-testid="tpl-disclaimer" /></div>

      <div className="flex justify-end pt-2 border-t">
        <Button onClick={save} disabled={saving} data-testid="tpl-save"><Save className="h-4 w-4 mr-2" />{saving ? 'Saving…' : 'Save template'}</Button>
      </div>
    </Card>
  );
}

function SMTPConfigCard() {
  const [f, setF] = useState({ enabled: false, host: '', port: 587, user: '', password: '', from_addr: '', use_tls: true });
  const [passSet, setPassSet] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get('/admin/smtp').then((r) => {
      setF({ enabled: r.data.enabled || false, host: r.data.host || '', port: r.data.port || 587, user: r.data.user || '', password: '', from_addr: r.data.from_addr || '', use_tls: r.data.use_tls !== false });
      setPassSet(!!r.data.password_set);
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try { await api.post('/admin/smtp', f); toast.success('SMTP saved'); }
    catch (e) { toast.error('Save failed'); }
    finally { setSaving(false); }
  };
  const testIt = async () => {
    if (!testTo) return toast.error('Enter a recipient to test');
    setTesting(true);
    try {
      const r = await api.post('/admin/smtp/test', { to: testTo });
      if (r.data.status === 'sent') toast.success('Test email sent!');
      else toast.error(r.data.reason || r.data.status);
    } catch (e) { toast.error('Test failed'); }
    finally { setTesting(false); }
  };

  return (
    <Card className="p-5 max-w-3xl space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-display font-semibold">Email delivery (SMTP)</p>
          <p className="text-sm text-muted-foreground">Configure your Gmail/Outlook/SES SMTP to actually deliver emails. No restart needed.</p>
        </div>
        <label className="flex items-center gap-2"><Switch checked={f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} data-testid="smtp-enabled" /><span className="text-sm">Enabled</span></label>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <div className="space-y-1"><Label>SMTP host</Label><Input value={f.host} onChange={(e) => setF({ ...f, host: e.target.value })} placeholder="smtp.gmail.com" data-testid="smtp-host" /></div>
        <div className="space-y-1"><Label>Port</Label><Input type="number" value={f.port} onChange={(e) => setF({ ...f, port: +e.target.value })} data-testid="smtp-port" /></div>
        <div className="space-y-1"><Label>Username / email</Label><Input value={f.user} onChange={(e) => setF({ ...f, user: e.target.value })} placeholder="you@company.com" data-testid="smtp-user" /></div>
        <div className="space-y-1"><Label>Password / App password</Label><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder={passSet ? 'Leave blank to keep existing' : 'App password'} data-testid="smtp-password" /></div>
        <div className="space-y-1"><Label>From address</Label><Input value={f.from_addr} onChange={(e) => setF({ ...f, from_addr: e.target.value })} placeholder="Defaults to username" data-testid="smtp-from" /></div>
        <div className="space-y-1 flex items-end"><label className="flex items-center gap-2"><Switch checked={f.use_tls} onCheckedChange={(v) => setF({ ...f, use_tls: v })} data-testid="smtp-tls" /><span className="text-sm">Use STARTTLS</span></label></div>
      </div>
      <div className="flex items-center justify-end gap-2 pt-2 border-t">
        <div className="flex items-center gap-2 mr-auto">
          <Input placeholder="Send test to email…" value={testTo} onChange={(e) => setTestTo(e.target.value)} className="h-9 w-64" data-testid="smtp-test-to" />
          <Button size="sm" variant="outline" onClick={testIt} disabled={testing || !f.enabled} data-testid="smtp-test-btn">{testing ? 'Testing…' : 'Send test'}</Button>
        </div>
        <Button onClick={save} disabled={saving} data-testid="smtp-save"><Save className="h-4 w-4 mr-2" />{saving ? 'Saving…' : 'Save'}</Button>
      </div>
      <div className="rounded-md bg-muted/40 p-3 text-xs text-muted-foreground">
        <p className="font-medium text-foreground mb-1">Gmail hints</p>
        <p>Use <span className="font-mono">smtp.gmail.com</span> on port <span className="font-mono">587</span> with an <a className="text-primary underline" href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer">App Password</a> (not your normal password). Enable 2FA first.</p>
      </div>
    </Card>
  );
}
